import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import {
  datahubAxiosClient,
  ENDPOINT_DETAILS,
  ORDER_GET_TOTAL_TASKS_ENDPOINT,
  ORDER_GET_SOCIAL_MEDIA_OPTIONS_ENDPOINT,
  ORDER_CREATE_NEW_ORDER_ENDPOINT_V2,
} from 'constants/server/apiEndPoint';
import { ORDER_STATUS } from 'constants/ui/status';
import { mapDatahubTaskFromDto } from 'api/tasks/helpers';
import { IDatahubTask } from 'api/tasks/models';
import { mapBookingInfoFromDto, mapDatahubOrderLineFromDto } from './helpers';
import { CreateNewOrderPayload, IBookingInfo, IDatahubOrderLine } from './models';
import { ICreateNewOrderResponseDto, IDatahubOrderLineDto } from './dtos';
import { useLocalization } from 'localization';
import notifyHandler from 'utils/notifyHandler';
import { ApiMutationFn } from 'types/apiParamsTypes';
import { IDatahubWatcherDto } from 'api/users/dtos';
import { ICommonResponse } from 'api/types';
import { casesQueryKey } from 'api/cases/query';
import { useToastMessageCopyable } from 'hooks/general/useToastMessageCopyable';
import { SelectOption } from 'components/v2/input/InputSelectField';
import { IDatahubTaskDto } from 'api/tasks/dtos';

type OpenOrder = {
  count: number;
};

type AddOrderLine = {
  itemId: string;
  config?: string;
  quantity: number;
  variant?: string;
};

type OrderPayload = Partial<{
  orderId: string;
  addOrderLinePayload: AddOrderLine[];
  responsibleAgentId: string;
  watcherIds: string[];
  caseId: string;
}> &
  ApiMutationFn;

export const ordersQueryKey = {
  all: ['orders'] as const,
  createOrderResponse: ['createOrderResponse'] as const,
  countOpenOrders: () => [...ordersQueryKey.all, 'countOrders'] as const,
  orderTasks: () => [...ordersQueryKey.all, 'orderTasks'] as const,
  bookingInfo: () => [...ordersQueryKey.all, 'bookingInfo'] as const,
  socialMediaTypes: () => [...ordersQueryKey.all, 'socialMedia'] as const,
  orderLines: (orderId: string) => [orderId, 'orderLines'] as const,
  orderWatchers: (orderId: string) => [orderId, 'orderWatchers'] as const,
  countOpenTasks: () => [...ordersQueryKey.all, 'countOpenTasks'] as const,
  getPropertyTypes: () => [...ordersQueryKey.all, 'getPropertyTypes'] as const,
  getContactPersonTypes: () => [...ordersQueryKey.all, 'getContactPersonTypes'] as const,
};

export const countOrderTasksApi = () => {
  const { data, isLoading, isFetching, isError } = useQuery<number>({
    queryKey: ordersQueryKey.countOpenTasks(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ORDER_GET_TOTAL_TASKS_ENDPOINT);
      return response.data;
    },
  });

  return { data, isLoading, isFetching, isError };
};

export const countOrderApi = () => {
  const { data, isLoading, isFetching, isError } = useQuery<OpenOrder>({
    queryKey: ordersQueryKey.countOpenOrders(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getOpenTasks(ORDER_STATUS.OPEN));
      return response.data;
    },
  });

  return { data, isLoading, isFetching, isError };
};

export const getOrderTasksApi = (orderId: string) => {
  const { data, isLoading, isFetching, isError } = useQuery<IDatahubTask[]>({
    queryKey: ordersQueryKey.orderTasks(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getOrderTasks(orderId));
      return response?.data?.map(mapDatahubTaskFromDto) || [];
    },
    enabled: !!orderId,
  });

  return { data, isLoading, isFetching, isError };
};

export const getBookingInfoByOrderIdApi = (orderId: string) => {
  const { data, isLoading, isFetching, isError } = useQuery<IBookingInfo | undefined>({
    queryKey: ordersQueryKey.bookingInfo(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getBookingInfo(orderId));
      return response?.data ? mapBookingInfoFromDto(response.data) : undefined;
    },
    enabled: !!orderId,
  });

  return { data, isLoading, isFetching, isError };
};

export const getSocialMediaTypesApi = () => {
  const { data, isLoading, isFetching, isError, isRefetching, refetch } = useQuery<string[]>({
    queryKey: ordersQueryKey.socialMediaTypes(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ORDER_GET_SOCIAL_MEDIA_OPTIONS_ENDPOINT);
      return response.data;
    },
  });

  return { data, isLoading, isFetching, isError, isRefetching, refetch };
};

//new order version
export const createNewOrderMutationV2 = (payload: OrderPayload) => {
  const { t } = useLocalization();
  const { handleSuccess } = payload;
  const queryClient = useQueryClient();
  const {
    mutateAsync,
    isPending: isLoading,
    data,
    isSuccess,
  } = useMutation({
    mutationFn: async (payload: CreateNewOrderPayload) => {
      const response = await datahubAxiosClient.post<ICreateNewOrderResponseDto>(ORDER_CREATE_NEW_ORDER_ENDPOINT_V2, {
        ...payload,
      });

      return response;
    },
    onError: (error, variables) => {
      const traceId = (error as any)?.response?.headers?.['x-trace-id'];
      useToastMessageCopyable({
        message: t('orders.pages.new.createOrderFailed'),
        copyLabel: `TraceID: ${traceId}`,
        copyText: traceId,
        duration: 10000,
      });

      Sentry.addBreadcrumb({
        message: 'Create order failed',
        level: 'error',
        data: {
          payload: variables,
          error: error,
        },
      });

      Sentry.captureException(error, {
        tags: {
          operation: 'create_order',
        },
        contexts: {
          payload: variables,
        },
      });
    },
    onSuccess: (response, variables) => {
      const data = response.data;
      const traceId = response?.headers?.['x-trace-id'];

      if (!data.success) {
        useToastMessageCopyable({
          message: t('orders.pages.new.createOrderFailed'),
          copyLabel: `TraceID: ${traceId}`,
          copyText: traceId,
          duration: 10000,
        });

        const err = new Error(JSON.stringify(response));

        Sentry.addBreadcrumb({
          message: 'Create order failed',
          level: 'error',
          data: {
            payload: variables,
            error: data.errors,
          },
        });
        Sentry.captureException(err, {
          tags: {
            operation: 'create_order',
          },
          contexts: {
            payload: variables,
          },
        });
        return;
      }

      queryClient.setQueryData(ordersQueryKey.createOrderResponse, response);
      handleSuccess?.();
      notifyHandler({ type: 'success', content: t('common.terms.success') });

      Sentry.addBreadcrumb({
        message: 'Create order successful',
        level: 'info',
        data: {
          payload: variables,
          response: response,
        },
      });
      Sentry.captureMessage('Order created successfully', 'info');
    },
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const getOrderLinesApi = (orderId: string) => {
  const { data, isLoading, isFetching, isError } = useQuery<IDatahubOrderLine[]>({
    queryKey: ordersQueryKey.orderLines(orderId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get<IDatahubOrderLineDto[]>(ENDPOINT_DETAILS.getOrderLines(orderId));
      return response?.data ? response?.data?.map(mapDatahubOrderLineFromDto) : [];
    },
    enabled: !!orderId,
  });

  return { data, isLoading, isFetching, isError };
};

export const getTaskDetailsNoCacheApi = async (taskId: string) => {
  const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getTaskDetails(taskId));
  return response.data ? mapDatahubTaskFromDto(response.data as IDatahubTaskDto) : null;
};

export const addOrderLinesMutation = (payload: OrderPayload) => {
  const { handleSuccess, orderId, addOrderLinePayload } = payload;
  const { t } = useLocalization();
  const {
    mutateAsync,
    isPending: isLoading,
    data,
    isSuccess,
  } = useMutation({
    mutationKey: ordersQueryKey.orderLines(orderId ?? ''),
    mutationFn: async () => {
      const response = await datahubAxiosClient.post<{ success: boolean }[]>(
        ENDPOINT_DETAILS.addOrderLine(orderId ?? ''),
        addOrderLinePayload
      );

      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('modals.addExtraOrderLine.orderUpdateError') });
    },
    onSuccess: res => {
      if (res?.some(({ success }) => !success)) {
        return notifyHandler({ type: 'error', content: t('modals.addExtraOrderLine.orderUpdateError') });
      }
      notifyHandler({ type: 'success', content: t('modals.addExtraOrderLine.orderUpdateSuccess') });
      handleSuccess?.();
    },
  });

  return { mutateAsync, isLoading, data, isSuccess };
};

export const getOrderWatchersApi = (orderId: string) => {
  const { data, isLoading, isFetching, isError } = useQuery<IDatahubWatcherDto[]>({
    queryKey: ordersQueryKey.orderWatchers(orderId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.orderWatchers(orderId));
      return response?.data;
    },
    enabled: !!orderId,
  });

  return { data, isLoading, isFetching, isError };
};

export const updateResponsibleAgentMutation = (payload: OrderPayload) => {
  const { handleSuccess, orderId, responsibleAgentId, caseId } = payload;
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const {
    mutateAsync,
    isPending: isLoading,
    data,
    isSuccess,
  } = useMutation({
    mutationFn: async () => {
      const response = await datahubAxiosClient.post<ICommonResponse>(
        ENDPOINT_DETAILS.updateResponsibleAgent(orderId ?? ''),
        {
          userId: responsibleAgentId,
        }
      );

      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('orders.pages.details.responsibleUpdateError') });
    },
    onSuccess: res => {
      if (!res?.success) {
        return notifyHandler({ type: 'error', content: t('orders.pages.details.responsibleUpdateError') });
      }
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      handleSuccess?.();
      queryClient.invalidateQueries({ queryKey: casesQueryKey.ordersInCase(caseId ?? '') });
    },
  });
  return { updateResponsibleAgentAsync: mutateAsync, isUpdatingResponsibleAgent: isLoading, data, isSuccess };
};

export const updateWatchersMutation = (payload: OrderPayload) => {
  const { handleSuccess, orderId, watcherIds } = payload;
  const { t } = useLocalization();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: async () => {
      const response = await datahubAxiosClient.post<ICommonResponse>(ENDPOINT_DETAILS.orderWatchers(orderId ?? ''), {
        userIds: watcherIds,
      });

      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('orders.pages.details.watchersUpdateError') });
    },
    onSuccess: res => {
      if (!res?.success) {
        return notifyHandler({ type: 'error', content: t('orders.pages.details.watchersUpdateError') });
      }
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      handleSuccess?.();
      queryClient.invalidateQueries({ queryKey: ordersQueryKey.orderWatchers(orderId ?? '') });
    },
  });
  return { updateWatchersAsync: mutateAsync, isUpdatingWatchers: isLoading };
};

export const getPropertyTypesApi = () => {
  const { t } = useLocalization();

  const { data, isLoading, isFetching, isError } = useQuery<SelectOption[]>({
    queryKey: ordersQueryKey.getPropertyTypes(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get<string[]>(ENDPOINT_DETAILS.getPropertyTypes());
      return response?.data
        .map<SelectOption>(term => ({
          label: t(`orders.propertyTypesOptions.${term}`),
          value: term ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  });

  return { data, isLoading, isFetching, isError };
};

export const getContactPersonTypesApi = () => {
  const { t } = useLocalization();

  const { data, isLoading, isFetching, isError } = useQuery<SelectOption[]>({
    queryKey: ordersQueryKey.getContactPersonTypes(),
    queryFn: async () => {
      const response = await datahubAxiosClient.get<string[]>(ENDPOINT_DETAILS.getContactPersonTypes());
      const transformTerms = response?.data
        ?.map<SelectOption>(term => ({
          label: t(`orders.contactPersons.types.${term}`),
          value: term ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      return transformTerms;
    },
  });

  return { data, isLoading, isFetching, isError };
};
